import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IoCartOutline, IoClose } from "react-icons/io5";
import { Toast as ToastData, useCart } from "../context/CartContext";
import styles from "./Toast.module.css";

const VISIBLE_MS = 4000;
/** Must match the exit transition in Toast.module.css. */
const EXIT_MS = 160;

interface ToastCardProps {
  toast: ToastData;
  onDismiss: (id: number) => void;
}

/** One auto-dismissing card. Owns its own timers so the queue stays dumb. */
const ToastCard: FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const [leaving, setLeaving] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setLeaving(true);
    exitTimer.current = setTimeout(() => onDismiss(toast.id), EXIT_MS);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const timer = setTimeout(close, VISIBLE_MS);
    return () => {
      clearTimeout(timer);
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, [close]);

  return (
    <div
      className={`ui-card ${styles.toast} ${leaving ? styles.leaving : ""}`}
      role="status"
    >
      <span className={`ui-icon-chip ${styles.chip}`} aria-hidden="true">
        <IoCartOutline size={18} />
      </span>

      <div className={styles.body}>
        <p className={styles.title}>{toast.title}</p>
        {toast.detail && <p className={styles.detail}>{toast.detail}</p>}
        {toast.cartLink && (
          <Link
            className={`ui-btn ui-btn--ghost ui-btn--sm ${styles.action}`}
            to="/shopping/cart"
            onClick={close}
          >
            View cart
          </Link>
        )}
      </div>

      <button
        type="button"
        className={`ui-icon-btn ${styles.close}`}
        onClick={close}
        aria-label="Dismiss notification"
      >
        <IoClose size={20} aria-hidden="true" />
      </button>
    </div>
  );
};

/**
 * Renders the cart's toast queue.
 */
export const ToastContainer: FC = () => {
  const { toasts, dismissToast } = useCart();

  if (toasts.length === 0) return null;

  return (
    <div className={styles.container}>
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
};

export default ToastContainer;
